// SVG goban. Layers (bottom to top): wood + grain, grid, hoshi, coordinates,
// territory / ownership overlay, stones (memoized), fading captures, dame
// marks, last-move marker, suggestion ghosts, pending-confirm ghost, hover
// ghost, transparent hit targets.

import { memo, useMemo, useState } from 'react';
import type { AnalysisMoveInfo, Color, Pt, Vertex } from '../../shared/types.ts';
import { BLACK, WHITE, type Position } from '../../shared/go/board.ts';
import { GTP_COLUMNS, ptToVertex, vertexToPt, isBoardVertex } from '../../shared/go/coords.ts';
import { checkMove } from '../../shared/go/rules.ts';
import type { FadingCapture } from '../store/useStore.ts';

export type BoardMode = 'play' | 'scoring' | 'inert';

export interface GobanProps {
  size: number;
  position: Position;
  toPlay: Color;
  mode: BoardMode;
  lastMove: Vertex | null;
  deadStones: ReadonlySet<Vertex>;
  dameMarks: ReadonlySet<Vertex>;
  territory: { B: readonly Vertex[]; W: readonly Vertex[] } | null;
  ownership: readonly number[] | null;
  suggestions: readonly AnalysisMoveInfo[] | null;
  fadingCaptures: readonly FadingCapture[];
  pendingConfirm: Vertex | null;
  onPointClick?: ((vertex: Vertex) => void) | undefined;
}

function hoshiPoints(size: number): Array<[number, number]> {
  if (size === 19) {
    const s = [3, 9, 15];
    return s.flatMap((x) => s.map((y) => [x, y] as [number, number]));
  }
  if (size === 13) {
    return [
      [3, 3],
      [3, 9],
      [9, 3],
      [9, 9],
      [6, 6],
    ];
  }
  if (size === 9) {
    return [
      [2, 2],
      [2, 6],
      [6, 2],
      [6, 6],
      [4, 4],
    ];
  }
  return [];
}

function xyOf(pt: Pt, size: number): [number, number] {
  const x = pt % size;
  return [x, (pt - x) / size];
}

interface StonesLayerProps {
  stones: Uint8Array;
  size: number;
  dead: ReadonlySet<Vertex>;
}

const StonesLayer = memo(function StonesLayer({ stones, size, dead }: StonesLayerProps) {
  const nodes = [];
  for (let pt = 0; pt < stones.length; pt++) {
    const state = stones[pt];
    if (state !== BLACK && state !== WHITE) continue;
    const [x, y] = xyOf(pt, size);
    const vertex = ptToVertex(pt, size);
    const isDead = dead.has(vertex);
    nodes.push(
      <g key={pt} className={isDead ? 'stone stone-dead' : 'stone'}>
        <circle
          cx={x}
          cy={y}
          r={0.47}
          fill={state === BLACK ? 'url(#stone-black)' : 'url(#stone-white)'}
          stroke={state === WHITE ? 'rgba(60,48,30,0.55)' : 'none'}
          strokeWidth={0.018}
          filter="url(#stone-shadow)"
        />
        {isDead && (
          <path
            d={`M ${x - 0.2} ${y - 0.2} L ${x + 0.2} ${y + 0.2} M ${x - 0.2} ${y + 0.2} L ${x + 0.2} ${y - 0.2}`}
            stroke="var(--vermillion)"
            strokeWidth={0.07}
            strokeLinecap="round"
          />
        )}
      </g>,
    );
  }
  return <g>{nodes}</g>;
});

const SUGGESTION_BADGES = 'ABCDEF';

export function Goban(props: GobanProps) {
  const {
    size,
    position,
    toPlay,
    mode,
    lastMove,
    deadStones,
    dameMarks,
    territory,
    ownership,
    suggestions,
    fadingCaptures,
    pendingConfirm,
    onPointClick,
  } = props;

  const [hoverPt, setHoverPt] = useState<Pt | null>(null);

  const pad = 1.25;
  const extent = size - 1 + 2 * pad;
  const viewBox = `${-pad} ${-pad} ${extent} ${extent}`;

  const hoshi = useMemo(() => hoshiPoints(size), [size]);

  const hoverLegal =
    hoverPt !== null && mode === 'play' ? checkMove(position, toPlay, hoverPt) === null : false;

  const gridLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i < size; i++) {
      const edge = i === 0 || i === size - 1 ? 0.042 : 0.024;
      lines.push(
        <line key={`h${i}`} x1={0} y1={i} x2={size - 1} y2={i} stroke="#4A3517" strokeWidth={edge} />,
        <line key={`v${i}`} x1={i} y1={0} x2={i} y2={size - 1} stroke="#4A3517" strokeWidth={edge} />,
      );
    }
    return lines;
  }, [size]);

  const coordLabels = useMemo(() => {
    const labels = [];
    for (let x = 0; x < size; x++) {
      labels.push(
        <text key={`c${x}`} x={x} y={size - 1 + 0.95} className="goban-coord" textAnchor="middle">
          {GTP_COLUMNS.charAt(x)}
        </text>,
      );
    }
    for (let y = 0; y < size; y++) {
      labels.push(
        <text
          key={`r${y}`}
          x={-0.92}
          y={y + 0.11}
          className="goban-coord"
          textAnchor="middle"
        >
          {size - y}
        </text>,
      );
    }
    return labels;
  }, [size]);

  const hitTargets = useMemo(() => {
    const rects = [];
    for (let pt = 0; pt < size * size; pt++) {
      const [x, y] = xyOf(pt, size);
      rects.push(
        <rect
          key={pt}
          x={x - 0.5}
          y={y - 0.5}
          width={1}
          height={1}
          fill="transparent"
          data-pt={pt}
        />,
      );
    }
    return rects;
  }, [size]);

  const ptFromEvent = (e: React.SyntheticEvent): Pt | null => {
    const target = e.target as SVGElement;
    const raw = target.dataset?.pt;
    if (raw === undefined) return null;
    const pt = Number(raw);
    return Number.isInteger(pt) ? pt : null;
  };

  const interactive = mode !== 'inert' && onPointClick !== undefined;

  // Ownership heatmap (mid-game territory estimate).
  const ownershipRects = useMemo(() => {
    if (!ownership || ownership.length !== size * size) return null;
    const rects = [];
    for (let pt = 0; pt < ownership.length; pt++) {
      const o = ownership[pt]!;
      if (Math.abs(o) < 0.08) continue;
      const [x, y] = xyOf(pt, size);
      rects.push(
        <rect
          key={pt}
          x={x - 0.45}
          y={y - 0.45}
          width={0.9}
          height={0.9}
          rx={0.1}
          fill={o > 0 ? '#0E0C09' : '#F1E8D7'}
          opacity={Math.min(0.72, Math.abs(o) * 0.62)}
        />,
      );
    }
    return rects;
  }, [ownership, size]);

  // Crisp territory marks (scoring phase / finished breakdown).
  const territoryRects = useMemo(() => {
    if (!territory) return null;
    const mk = (verts: readonly Vertex[], color: Color) =>
      verts.filter(isBoardVertex).map((v) => {
        const [x, y] = xyOf(vertexToPt(v, size), size);
        return (
          <rect
            key={`${color}${v}`}
            x={x - 0.17}
            y={y - 0.17}
            width={0.34}
            height={0.34}
            rx={0.05}
            fill={color === 'B' ? '#0E0C09' : '#F1E8D7'}
            opacity={0.88}
            stroke={color === 'B' ? 'none' : 'rgba(60,48,30,0.5)'}
            strokeWidth={0.015}
          />
        );
      });
    return [...mk(territory.B, 'B'), ...mk(territory.W, 'W')];
  }, [territory, size]);

  const dameNodes = useMemo(() => {
    const nodes = [];
    for (const v of dameMarks) {
      if (!isBoardVertex(v)) continue;
      const pt = vertexToPt(v, size);
      if (position.stones[pt] !== 0) continue;
      const [x, y] = xyOf(pt, size);
      nodes.push(
        <path
          key={v}
          d={`M ${x} ${y - 0.22} L ${x + 0.22} ${y} L ${x} ${y + 0.22} L ${x - 0.22} ${y} Z`}
          fill="none"
          stroke="var(--amber)"
          strokeWidth={0.07}
          strokeLinejoin="round"
        />,
      );
    }
    return nodes;
  }, [dameMarks, position, size]);

  // Last-move marker.
  let lastMoveNode = null;
  if (lastMove && isBoardVertex(lastMove)) {
    const pt = vertexToPt(lastMove, size);
    const state = position.stones[pt];
    if (state === BLACK || state === WHITE) {
      const [x, y] = xyOf(pt, size);
      lastMoveNode = (
        <circle
          cx={x}
          cy={y}
          r={0.21}
          fill="none"
          stroke={state === BLACK ? '#E8E0D2' : '#221E19'}
          strokeWidth={0.062}
        />
      );
    }
  }

  // Suggestion ghosts with A/B/C badges + score-delta text (mover perspective).
  const suggestionNodes = useMemo(() => {
    if (!suggestions || suggestions.length === 0) return null;
    const sign = toPlay === 'B' ? 1 : -1;
    const sorted = [...suggestions].sort((a, b) => a.order - b.order).slice(0, 5);
    const best = sorted[0];
    if (!best) return null;
    return sorted.map((info, i) => {
      if (!isBoardVertex(info.vertex)) return null;
      const pt = vertexToPt(info.vertex, size);
      if (position.stones[pt] !== 0) return null;
      const [x, y] = xyOf(pt, size);
      const delta = sign * info.scoreLead - sign * best.scoreLead;
      const deltaText = i === 0 ? 'best' : delta.toFixed(1);
      const ink = toPlay === 'B' ? '#EFE7D6' : '#191613';
      return (
        <g key={info.vertex} className="suggestion-ghost">
          <circle
            cx={x}
            cy={y}
            r={0.44}
            fill={toPlay === 'B' ? '#141210' : '#EFE7D6'}
            opacity={0.78}
          />
          <circle cx={x} cy={y} r={0.44} fill="none" stroke="var(--amber)" strokeWidth={0.045} />
          <text x={x} y={y + 0.02} textAnchor="middle" className="suggestion-badge" fill={ink}>
            {SUGGESTION_BADGES.charAt(i)}
          </text>
          <text x={x} y={y + 0.3} textAnchor="middle" className="suggestion-delta" fill={ink}>
            {deltaText}
          </text>
        </g>
      );
    });
  }, [suggestions, toPlay, position, size]);

  // Pending two-step confirmation ghost.
  let confirmNode = null;
  if (pendingConfirm && isBoardVertex(pendingConfirm)) {
    const pt = vertexToPt(pendingConfirm, size);
    const [x, y] = xyOf(pt, size);
    confirmNode = (
      <g className="confirm-ghost">
        <circle
          cx={x}
          cy={y}
          r={0.47}
          fill={toPlay === 'B' ? 'url(#stone-black)' : 'url(#stone-white)'}
          opacity={0.75}
        />
        <circle cx={x} cy={y} r={0.58} fill="none" stroke="var(--vermillion)" strokeWidth={0.05} />
      </g>
    );
  }

  // Hover ghost (legality-aware).
  let hoverNode = null;
  if (hoverPt !== null && mode === 'play' && hoverLegal && hoverPt !== (pendingConfirm && isBoardVertex(pendingConfirm) ? vertexToPt(pendingConfirm, size) : -1)) {
    const [x, y] = xyOf(hoverPt, size);
    hoverNode = (
      <circle
        className="hover-ghost"
        cx={x}
        cy={y}
        r={0.45}
        fill={toPlay === 'B' ? 'url(#stone-black)' : 'url(#stone-white)'}
        opacity={0.42}
      />
    );
  }
  let scoringHoverNode = null;
  if (hoverPt !== null && mode === 'scoring') {
    const [x, y] = xyOf(hoverPt, size);
    scoringHoverNode = (
      <circle cx={x} cy={y} r={0.5} fill="none" stroke="var(--amber)" strokeWidth={0.04} opacity={0.8} />
    );
  }

  const fadingNodes = fadingCaptures
    .filter((f) => isBoardVertex(f.vertex))
    .map((f) => {
      const [x, y] = xyOf(vertexToPt(f.vertex, size), size);
      return (
        <circle
          key={f.key}
          className="stone-capture-fade"
          cx={x}
          cy={y}
          r={0.47}
          fill={f.color === 'B' ? 'url(#stone-black)' : 'url(#stone-white)'}
        />
      );
    });

  const blackCount = position.stones.reduce((n, s) => n + (s === BLACK ? 1 : 0), 0);
  const whiteCount = position.stones.reduce((n, s) => n + (s === WHITE ? 1 : 0), 0);

  const cursorClass =
    mode === 'inert'
      ? 'goban goban-inert'
      : hoverPt !== null && mode === 'play' && !hoverLegal
        ? 'goban goban-noplay'
        : 'goban';

  return (
    <svg
      viewBox={viewBox}
      className={cursorClass}
      role="img"
      aria-label={`Go board, ${size} by ${size}, ${blackCount} black and ${whiteCount} white stones, ${toPlay === 'B' ? 'Black' : 'White'} to play`}
    >
      <defs>
        <radialGradient id="stone-black" cx="0.36" cy="0.32" r="0.85">
          <stop offset="0%" stopColor="#5D5A55" />
          <stop offset="55%" stopColor="#25231F" />
          <stop offset="100%" stopColor="#0B0A08" />
        </radialGradient>
        <radialGradient id="stone-white" cx="0.38" cy="0.32" r="0.9">
          <stop offset="0%" stopColor="#FFFDF6" />
          <stop offset="70%" stopColor="#EDE4D0" />
          <stop offset="100%" stopColor="#CFC2A6" />
        </radialGradient>
        <linearGradient id="kaya" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#DBA85C" />
          <stop offset="100%" stopColor="#C08B3F" />
        </linearGradient>
        <filter id="stone-shadow" x="-30%" y="-30%" width="160%" height="170%">
          <feDropShadow dx="0.015" dy="0.045" stdDeviation="0.035" floodColor="#1B1104" floodOpacity="0.55" />
        </filter>
        <filter id="wood-grain" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.55 0.035" numOctaves="2" seed="11" result="noise" />
          <feColorMatrix
            in="noise"
            type="matrix"
            values="0 0 0 0 0.28  0 0 0 0 0.17  0 0 0 0 0.05  0 0 0 0.5 0"
          />
        </filter>
      </defs>

      {/* wood */}
      <rect
        x={-0.78}
        y={-0.78}
        width={size - 1 + 1.56}
        height={size - 1 + 1.56}
        rx={0.12}
        fill="url(#kaya)"
      />
      <rect
        x={-0.78}
        y={-0.78}
        width={size - 1 + 1.56}
        height={size - 1 + 1.56}
        rx={0.12}
        filter="url(#wood-grain)"
        opacity={0.16}
      />

      <g>{gridLines}</g>
      <g>
        {hoshi.map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={size >= 13 ? 0.1 : 0.11} fill="#4A3517" />
        ))}
      </g>
      <g aria-hidden="true">{coordLabels}</g>

      {ownershipRects && <g className="overlay-ownership">{ownershipRects}</g>}
      {territoryRects && <g className="overlay-territory">{territoryRects}</g>}

      <StonesLayer stones={position.stones} size={size} dead={deadStones} />
      <g>{fadingNodes}</g>
      <g>{dameNodes}</g>
      {lastMoveNode}
      {suggestionNodes && <g>{suggestionNodes}</g>}
      {confirmNode}
      {hoverNode}
      {scoringHoverNode}

      {interactive && (
        <g
          onClick={(e) => {
            const pt = ptFromEvent(e);
            if (pt !== null && onPointClick) onPointClick(ptToVertex(pt, size));
          }}
          onMouseOver={(e) => {
            const pt = ptFromEvent(e);
            if (pt !== null) setHoverPt(pt);
          }}
          onMouseOut={() => setHoverPt(null)}
        >
          {hitTargets}
        </g>
      )}
    </svg>
  );
}
